
<template>
	<TheStdLayout :title="$t('Preferences')">
		<PreferencesAuthorProfileDrawer
			v-if="showProfileEditor"
			@requestUnmount="closeAuthorProfileEditor"
		/>

		<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">
			<div class="settings-group">
				<h2>{{ $t('General') }}</h2>
				<div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Profile picture') }}</h3>
						<p>{{ $t('Smart Symbols loads the profile picture of your {provider} account. To change it, you’ll need to change the picture of your {provider} account.', { provider: userAuthProvider }) }}</p>
					</div>
					<div class="setting-control">
						<UserAvatar :user="me" :usePublicProfile="false" class="w-20 h-20"/>
					</div>
				</div>
				<div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Language') }}</h3>
						<p>{{ $t('Change the language used in the user interface of this application') }}</p>
					</div>
					<div class="setting-control">
						<SelectRoot v-model="me.locale" @update:model-value="save">
							<SelectTrigger
								class="inline-flex items-center justify-between min-w-40 rounded-lg px-4 text-xs font-semibold leading-none h-9 bg-white hover:bg-base-100 outline-none"
								aria-label="Customise options"
							>
								<SelectValue placeholder="Select a language..." />
								<ChevronDownIcon class="h-3.5 w-3.5" />
							</SelectTrigger>

							<SelectPortal>
								<SelectContent
									class="min-w-40 bg-white rounded-lg border shadow-sm will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
									:side-offset="5"
								>
									<SelectScrollUpButton class="flex items-center justify-center h-6 bg-white cursor-default">
										<ChevronUpIcon class="h-3.5 w-3.5" />
									</SelectScrollUpButton>

									<SelectViewport class="p-2">
										<SelectGroup>
											<SelectItem
												v-for="(option, index) in languages"
												:key="index"
												class="text-xs leading-none rounded flex items-center h-6 pr-9 pl-6 relative select-none data-[highlighted]:outline-none data-[highlighted]:bg-base-100"
												:class="option.value === me.locale ? 'font-bold' : ''"
												:value="option.value"
											>
												<SelectItemIndicator class="absolute left-0 w-6 inline-flex items-center justify-center">
													<CheckIcon class="h-3.5 w-3.5" />
												</SelectItemIndicator>
												<SelectItemText>
													{{ option.label }}
												</SelectItemText>
											</SelectItem>
										</SelectGroup>
									</SelectViewport>

									<SelectScrollDownButton class="flex items-center justify-center h-6 bg-white cursor-default">
										<ChevronDownIcon class="h-3.5 w-3.5" />
									</SelectScrollDownButton>
								</SelectContent>
							</SelectPortal>
						</SelectRoot>
					</div>
				</div>
			</div>
			<div v-if="me?.authorType" class="settings-group">
				<h2>{{ $t('Content publishing') }}</h2>
				<div class="setting">
					<div class="setting-explanation">
						<h3>{{ $t('Public profile') }}</h3>
						<p>{{ $t('Your public profile is what others get to see about you when you share lesson plans publicly.') }}</p>

						<div class="mt-4 flex flex-col w-full md:grid md:grid-cols-[2fr_3fr] gap-4 border-2 border-slate-100 p-4 rounded-lg">
							<div class="flex items-center gap-6">
								<UserAvatar :user="me" :usePublicProfile="true" class="w-20 h-20"/>
								<div class="flex flex-col gap-1">
									<p class="font-semibold hyphens-auto">{{ me.publicName }}</p>
									<p class="text-base-content-light">{{ me.authorType }}</p>
								</div>
							</div>

							<div v-if="me?.publicDescription" class="prose prose-gray max-w-prose">
								<div class="text-base-content-light mb-6 text-sm">
									<p class="whitespace-pre-line public-profile-description" v-html="urlsToHrefs(me.publicDescription)"></p>
								</div>
							</div>
							<div v-else class="text-base-content-light italic text-sm">{{ $t('No description yet') }}</div>
						</div>

					</div>
					<div class="setting-control">
						<div class="btn btn-circle" @click="openAuthorProfileEditor($event)">
							<PencilIcon class="w-5 h-5" />
						</div>
					</div>
				</div>
				<!-- <div class="setting">
					<div class="setting-explanation">
						<h3>{{ $tc('Curriculum', 2)  }}</h3>
						<p>{{ $t('Select the curricula you need for authoring your lesson plans') }}</p>
					</div>
					<div class="setting-control">
						<ComboboxRoot
							class="relative"
							multiple
						>
							<ComboboxAnchor class="max-w-48 inline-flex gap-2.5 items-center justify-between rounded-lg px-4 text-xs leading-none h-9 hover:bg-base-100 outline-none">
								<ComboboxInput
									class="!bg-transparent outline-none h-full"
									:placeholder="$t('Select curricula...')"
								/>
								<ComboboxTrigger>
									<ChevronDownIcon class="h-3.5 w-3.5" />
								</ComboboxTrigger>
							</ComboboxAnchor>

							<ComboboxContent class="absolute mt-1 min-w-48 bg-white overflow-hidden rounded-lg shadow-sm border will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade">
								<ComboboxViewport class="p-2">
									<ComboboxEmpty class="text-xs font-medium text-center py-2" />
										<ComboboxItem
											v-for="(option, index) in languages"
											:key="option.label"
											:value="option.label"
											class="text-xs leading-none rounded flex items-center h-6 pr-9 pl-6 relative select-none data-[highlighted]:bg-base-100 data-[highlighted]:font-semibold"
										>
											<ComboboxItemIndicator
												class="absolute left-0 w-6 inline-flex items-center justify-center"
											>
												<CheckIcon class="h-3.5 w-3.5" />
											</ComboboxItemIndicator>
											<span>
												{{ option.label }}
											</span>
										</ComboboxItem>
								</ComboboxViewport>
							</ComboboxContent>
						</ComboboxRoot>
					</div>
				</div> -->
			</div>
		</div>
	</TheStdLayout>
</template>


<script lang="ts">
import { useUsersStore } from "@/stores/Users.store";
import { mapState, mapStores } from "pinia";
import { SUPPORTED_LOCALES } from "@/i18n.ts";
import PreferencesAuthorProfileDrawer from "@/components/PreferencesAuthorProfileDrawer.vue";
import { PencilIcon, ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { User } from "@/models/User.model";
import UserAvatar from "@/components/UserAvatar.vue";
import {
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectItemIndicator,
	SelectItemText,
	SelectPortal,
	SelectRoot,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectTrigger,
	SelectValue,
	SelectViewport,
	ComboboxAnchor,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxRoot,
	ComboboxTrigger,
	ComboboxViewport
} from 'reka-ui';

export default {

	data() {
		return {
			saving: false,
			showProfileEditor: false,
		}
	},

	components: {
		PencilIcon,
		ChevronDownIcon,
		ChevronUpIcon,
		CheckIcon,
		UserAvatar,
		PreferencesAuthorProfileDrawer,
		SelectContent,
		SelectGroup,
		SelectItem,
		SelectItemIndicator,
		SelectItemText,
		SelectPortal,
		SelectRoot,
		SelectScrollDownButton,
		SelectScrollUpButton,
		SelectTrigger,
		SelectValue,
		SelectViewport,
		ComboboxAnchor,
		ComboboxContent,
		ComboboxEmpty,
		ComboboxInput,
		ComboboxItem,
		ComboboxItemIndicator,
		ComboboxRoot,
		ComboboxTrigger,
		ComboboxViewport,
	},

	computed: {
		...mapStores(useUsersStore),
		...mapState(useUsersStore, ['me']),

		languages() {
			const out = [];
			for (let locale in SUPPORTED_LOCALES) {
				out.push({
					label: SUPPORTED_LOCALES[locale],
					value: locale
				})
			}
			return out;
		},

		userAuthProvider() {
			if (!this.me) return '';
			if(this.me.hasCapability('google-drive')) {
				return 'Google';
			} else if(this.me.hasCapability('onedrive')) {
				return 'Microsoft';
			} else {
				return this.$t('Identity provider');
			}
		},
	},

	methods: {
		// at the moment it saves only the language preference
		// but later could be used for save of curriculum and other preferences
		async save() {
			if (this.saving) {
				return;
			}

			this.saving = true;
			await this.usersStore.savePreferences(this.me as User);
			this.saving = false;
		},

		openAuthorProfileEditor() {
			this.showProfileEditor = true;
		},

		closeAuthorProfileEditor() {
			this.showProfileEditor = false;
		},

		// should probably be moved to a global util at some point
		urlsToHrefs(text: string): string {
			// Use the DOM to HTML-escape the raw text
			const div = document.createElement('div');
			div.textContent = text;
			const escapedText = div.innerHTML;
			// this will also result in & becoming &amp; in URL's, however that's not really an issue since browsers will interpret &amp; in hrefs as & anyway

			// Now safely replace URLs in the escaped string with anchor tags.
			return escapedText.replace(/https?:\/\/\S+|www\.\S+/gi, (url: string) => {
				// escape any trailing punctuation from the URL that shouldn't be part of the link (e.g., a period at the end of a sentence)
				const cleanUrl = url.replace(/[.,;:!?)'"]+$/, '');
				const href = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;
				return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
			});
		}
	}
}
</script>
